import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ApiProvider } from './contexts/ApiContext';
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Users from './components/Users';
import UserDetails from './components/UserDetails';
import DogOwners from './components/DogOwners';
import Dogs from './components/Dogs';
import DogDetails from './components/DogDetails';
import Posts from './components/Posts';
import DogGardens from './components/DogGardens';
import DogGardenDetails from './components/DogGardenDetails';
import Providers from './components/Providers';
import ProviderDetails from './components/ProviderDetails';
import ProviderImport from './components/ProviderImport';
import Unauthorized from './components/Unauthorized';
import PostDetails from './components/PostDetails';
import { ThemeProvider, createTheme, alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import AIAgentConfig from './components/AIAgentConfig';
import Reports from './components/Reports';
import PaymentReviews from './components/PaymentReviews';
import ProductCategories from './components/ProductCategories';
import LegalDocuments from './components/LegalDocuments';
import ProviderLandingConfig from './components/ProviderLandingConfig';
import AppVersionConfig from './components/AppVersionConfig';
import WebVersionGate from './components/WebVersionGate';
import VisibilityManagement from './components/VisibilityManagement';
import OrderDetails from './components/OrderDetails';
import Submissions from './components/Submissions';
import ProviderApplications from './components/ProviderApplications';
import CheckoutReview from './components/CheckoutReview';
import CitiesManagement from './components/CitiesManagement';
import DogAttributesManagement from './components/DogAttributesManagement';
import StoreCommission from './components/StoreCommission';
import StoreReturns from './components/StoreReturns';
import StorePayouts from './components/StorePayouts';

const theme = createTheme({
  palette: {
    primary: {
      main: '#6D28D9',
      light: '#8B5CF6',
      dark: '#5B21B6',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#9333EA',
      light: '#A855F7',
      dark: '#7C3AED',
    },
    background: {
      default: '#F8FAFC',
      paper: '#FFFFFF',
    },
    error:   { main: '#EF4444' },
    success: { main: '#10B981' },
    warning: { main: '#F59E0B' },
    info:    { main: '#3B82F6' },
    text: {
      primary:   '#111827',
      secondary: '#6B7280',
      disabled:  '#9CA3AF',
    },
    divider: '#E5E7EB',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif',
    h4: { fontWeight: 700, letterSpacing: '-0.02em' },
    h5: { fontWeight: 700, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: { fontWeight: 500, textTransform: 'none' },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          scrollbarColor: '#D1D5DB transparent',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: '#D1D5DB', borderRadius: 3 },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.07), 0 1px 2px -1px rgba(0,0,0,0.07)',
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        contained: {
          '&:hover': { boxShadow: '0 2px 8px rgba(109, 40, 217, 0.35)' },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.07), 0 1px 2px -1px rgba(0,0,0,0.07)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          fontSize: '0.75rem',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          backgroundColor: '#F9FAFB',
          color: '#6B7280',
          borderBottom: '1px solid #E5E7EB',
        },
        body: {
          borderBottom: '1px solid #F3F4F6',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 500, fontSize: '0.75rem' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 14 },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 12 },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 4 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <ApiProvider>
          <WebVersionGate />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/unauthorized" element={<Unauthorized />} />

            <Route element={<PrivateRoute />}>
              <Route element={<Layout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/users" element={<Users />} />
                <Route path="/users/:id" element={<UserDetails />} />
                <Route path="/dogs" element={<Dogs />} />
                <Route path="/dogs/:id" element={<DogDetails />} />
                <Route path="/dog-owners" element={<DogOwners />} />
                <Route path="/posts" element={<Posts />} />
                <Route path="/posts/:id" element={<PostDetails />} />
                <Route path="/dog-gardens" element={<DogGardens />} />
                <Route path="/dog-gardens/:id" element={<DogGardenDetails />} />
                <Route path="/providers" element={<Providers />} />
                <Route path="/providers/:id" element={<ProviderDetails />} />
                <Route path="/provider-import" element={<ProviderImport />} />
                <Route path="/provider-claims" element={<ProviderApplications />} />
                <Route path="/checkout-review" element={<CheckoutReview />} />
                {/* Old links keep working. */}
                <Route path="/provider-applications" element={<Navigate to="/provider-claims" replace />} />
                <Route path="/provider-landing-config" element={<ProviderLandingConfig />} />
                <Route path="/provider-visibility" element={<VisibilityManagement />} />
                <Route path="/orders/:id" element={<OrderDetails />} />
                <Route path="/ai-agent-config" element={<AIAgentConfig />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/payment-reviews" element={<PaymentReviews />} />
                <Route path="/submissions" element={<Submissions />} />
                <Route path="/product-categories" element={<ProductCategories />} />
                <Route path="/store-commission" element={<StoreCommission />} />
                <Route path="/store-returns" element={<StoreReturns />} />
                <Route path="/store-payouts" element={<StorePayouts />} />
                <Route path="/legal-documents" element={<LegalDocuments />} />
                <Route path="/app-version-config" element={<AppVersionConfig />} />
                <Route path="/cities-management" element={<CitiesManagement />} />
                <Route path="/pending-cities" element={<Navigate to="/cities-management" replace />} />
                <Route path="/dog-attributes" element={<DogAttributesManagement />} />
              </Route>
            </Route>

            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </ApiProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
